import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Spinner,
  Text,
  useTheme,
  useMediaQuery,
} from '@chakra-ui/react';
import AleaChevronDownIcon from '../../icons/AleaChevronDownIcon.tsx';
import { useTranslation } from 'react-i18next';

export interface AleaDropdownAlphaOption {
  value: string;
  label?: string;
  content?: React.ReactNode; // supports HTML/ReactNode for option area
  keywords?: string; // extra searchable text
}

interface AleaDropdownAlphaProps {
  label: string;
  value: string;
  options: AleaDropdownAlphaOption[];
  onChange: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  isDisabled?: boolean;
  loading?: boolean;
  error?: string;
  menuWidth?: string | number;
  dropdownZIndex?: number;
  menuPosition?: 'absolute' | 'fixed';
}

const AleaDropdownAlpha: React.FC<AleaDropdownAlphaProps> = ({
  label,
  value,
  options,
  onChange,
  placeholder = '-',
  searchPlaceholder = 'Search...',
  isDisabled = false,
  loading = false,
  error,
  menuWidth = '100%',
  dropdownZIndex = 1600,
  menuPosition = 'absolute',
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [isMobile] = useMediaQuery('(max-width: 768px)');
  
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [menuCoords, setMenuCoords] = useState<{ left: number; top: number }>({ left: 0, top: 0 });
  const [triggerWidth, setTriggerWidth] = useState<number>(0);
  const [computedMenuWidthPx, setComputedMenuWidthPx] = useState<number | null>(null);

  const selectedOption = useMemo(
    () => options.find((opt) => opt.value === value),
    [options, value]
  );

  const filteredOptions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((opt) => {
      const haystack = `${opt.label ?? ''} ${opt.value ?? ''} ${opt.keywords ?? ''}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [options, query]);

  useEffect(() => {
    // Close on outside click without using overlay
    const handleDocClick = (e: MouseEvent) => {
      if (!rootRef.current) return;
      if (isOpen && !rootRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', handleDocClick);
    return () => {
      document.removeEventListener('mousedown', handleDocClick);
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      // focus search input when opened
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [isOpen]);

  // Observe trigger width and keep it updated
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry?.contentRect?.width != null) {
        setTriggerWidth(entry.contentRect.width);
      }
    });
    ro.observe(el);
    // initialize immediately
    const rect = el.getBoundingClientRect();
    setTriggerWidth(rect.width);
    return () => {
      ro.disconnect();
    };
  }, []);

  const shouldUseWideMode = triggerWidth > 568;
  const effectiveMenuPosition = shouldUseWideMode ? 'absolute' : menuPosition;
  const effectiveMenuWidth = shouldUseWideMode ? '100%' : menuWidth;

  useEffect(() => {
    if (!isOpen || effectiveMenuPosition !== 'fixed') return;

    const recalc = () => {
      const triggerEl = rootRef.current;
      if (!triggerEl) return;
      const rect = triggerEl.getBoundingClientRect();

      // Resolve desired width in px
      let desiredWidthPx: number | null = null;
      if (typeof menuWidth === 'number') desiredWidthPx = menuWidth;
      else if (typeof menuWidth === 'string') {
        const m = menuWidth.match(/^(\d+)(px)?$/);
        desiredWidthPx = m ? parseInt(m[1], 10) : null;
      }
      // Fallback to trigger width if width cannot be resolved
      if (desiredWidthPx == null) desiredWidthPx = Math.round(rect.width);

      const viewportW = window.innerWidth;
      const safePadding = 8; // px, keep some space off edges
      const maxAllowedWidth = Math.max(0, viewportW - safePadding * 2);
      const finalWidth = Math.min(desiredWidthPx, maxAllowedWidth);

      // Default left aligned under trigger
      let left = rect.left;
      const right = left + finalWidth;
      const maxRight = viewportW - safePadding;

      // If overflows right edge, shift left
      if (right > maxRight) {
        left = Math.max(safePadding, maxRight - finalWidth);
      }
      // If overflows left edge, clamp
      if (left < safePadding) {
        left = safePadding;
      }

      setComputedMenuWidthPx(finalWidth);
      setMenuCoords({ left, top: rect.bottom });
    };

    recalc();
    window.addEventListener('scroll', recalc, true);
    window.addEventListener('resize', recalc);
    return () => {
      window.removeEventListener('scroll', recalc, true);
      window.removeEventListener('resize', recalc);
    };
  }, [isOpen, effectiveMenuPosition, menuWidth, triggerWidth]);

  return (
    <FormControl isInvalid={!!error} width="100%">
      <FormLabel
        sx={{
          fontWeight: 300,
          marginBottom: '10px',
          color: 'alea.500',
          paddingLeft: '10px',
          fontSize: '15px',
        }}
      >
        {label}
      </FormLabel>

      <Box position="relative" ref={rootRef}>
        <Flex
          alignItems="center"
          justifyContent="space-between"
          width="100%"
          borderTopLeftRadius="20px"
          borderBottomLeftRadius={isOpen ? '0' : '20px'}
          borderTopRightRadius="20px"
          borderBottomRightRadius={isOpen ? '0' : '20px'}
          bg="white"
          height="54px"
          minHeight="54px"
          cursor={isDisabled ? 'not-allowed' : 'text'}
          _hover={{ borderColor: isDisabled ? '#DCDCDC' : 'gray.300' }}
          p={0}
          overflow="hidden"
          opacity={isDisabled ? 0.6 : 1}
          aria-disabled={isDisabled}
          border="1px solid #DCDCDC"
          borderBottomWidth={isOpen ? '0px' : '1px'}
          onClick={() => {
            if (!isDisabled) {
              setIsOpen(true);
            }
          }}
        >
          <Flex flex="1" alignItems="center" height="100%" minWidth={0}>
            {isOpen ? (
              <Input
                ref={inputRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={searchPlaceholder}
                height="54px"
                border="none"
                borderRadius="0"
                _focus={{ boxShadow: 'none' }}
                px="10px"
              />
            ) : (
              <Text
                pl="10px"
                textAlign="left"
                color={isDisabled ? 'gray.400' : value ? 'black' : 'gray.500'}
                isTruncated
                width="100%"
                height="54px"
                lineHeight="54px"
                title={value
                  ? (selectedOption?.label ?? selectedOption?.value ?? value)
                  : placeholder}
              >
                {value
                  ? selectedOption?.label ?? selectedOption?.value ?? value
                  : placeholder}
              </Text>
            )}
          </Flex>

          <Flex
            alignItems="center"
            justifyContent="center"
            width="54px"
            height="54px"
            borderTopRightRadius="20px"
            borderBottomRightRadius="20px"
            color="white"
            cursor={isDisabled ? 'not-allowed' : 'pointer'}
            onClick={(e) => {
              e.stopPropagation();
              if (isDisabled) return;
              setIsOpen((prev) => !prev);
            }}
          >
            <Box
              as="span"
              transform={isOpen ? 'rotate(180deg)' : ''}
              color="white"
            >
              <AleaChevronDownIcon color={isDisabled ? '#A0AEC0' : theme.colors.alea[500]} />
            </Box>
          </Flex>
        </Flex>

        {/* Dropdown Menu (no overlay or fixed overlay based on prop) */}
        {isOpen && (
          <Box
            position={effectiveMenuPosition}
            top={effectiveMenuPosition === 'fixed' ? menuCoords.top : 'calc(100%)'}
            left={effectiveMenuPosition === 'fixed' ? menuCoords.left : 0}
            width={effectiveMenuPosition === 'fixed' ? (computedMenuWidthPx ?? effectiveMenuWidth) : effectiveMenuWidth}
            borderBottomLeftRadius="20px"
            borderBottomRightRadius="20px"
            bg="white"
            overflow="hidden"
            zIndex={dropdownZIndex}
            border="1px solid #DCDCDC"
            borderTopWidth="0px"
          >
            <Box
              maxHeight="260px"
              overflowY="auto"
              overflowX={isMobile ? 'auto' : 'hidden'}
            >
              <Box width="100%" minWidth={isMobile ? '640px' : undefined}>
                {loading && (
                  <Flex p={3} alignItems="center" gap={2}>
                    <Spinner size="sm" />
                    <Text>Loading...</Text>
                  </Flex>
                )}

                {!loading && filteredOptions.length === 0 && (
                  <Box p={3}>
                    <Text color="gray.500" textAlign="left">{t('general.noResults')}</Text>
                  </Box>
                )}

                {!loading &&
                  filteredOptions.map((opt, index) => {
                    const isSelected = opt.value === value;
                    return (
                      <Flex
                        key={opt.value}
                        alignItems="center"
                        justifyContent="space-between"
                        p="10px"
                        minHeight="54px"
                        cursor="pointer"
                        bg={isSelected ? 'white' : 'white'}
                        color={isSelected ? 'black' : 'black'}
                        fontWeight={isSelected ? '500' : '400'}
                        _hover={{
                          bg: isSelected ? 'gray.50' : 'gray.50',
                        }}
                        onClick={() => {
                          onChange(opt.value);
                          setIsOpen(false);
                          setQuery('');
                        }}
                        borderBottom={index === filteredOptions.length - 1 ? 'none' : '1px solid'}
                        borderBottomColor="#DCDCDC"
                        borderTop={index === 0 ? '1px solid' : 'none'}
                        borderTopColor="#DCDCDC"
                        width="100%"
                        flexShrink={0}
                      >
                        <Box textAlign="left" flex="1" minWidth={0}>
                          {opt.content ? (
                            opt.content
                          ) : (
                            <Text isTruncated title={opt.label ?? opt.value} lineHeight="1.3">
                              {opt.label ?? opt.value}
                            </Text>
                          )}
                        </Box>
                      </Flex>
                    );
                  })}
              </Box>
            </Box>
          </Box>
        )}
      </Box>

      {error && (
        <Box color="red.500" mt="2">
          {error}
        </Box>
      )}
    </FormControl>
  );
};

export default AleaDropdownAlpha;